import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getAccountAnimals, getAnimalById } from '../../api/animalsApi';
import { addSelectedSpecimenToCart } from '../../api/transactionCartApi';
import { Loader } from '../../components';
import AnimalInfo from '../../components/animal/AnimalInfo';
import BackButton from '../../components/back-button/BackButton';
import FilterDropdown, { FilterOption } from '../../components/custom-input/FilterDropdown';
import SelectSpecimens, { SelectedSpecimenQuantity } from '../../components/specimen-tables/SelectSpecimens';
import {
  ANIMAL_CONSTANTS,
  BUTTON_CONSTANTS,
  INVENTORY_MANAGEMENT_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
} from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { INVENTORY_PATHS, ROUTE_PATHS } from '../../constants/routePaths';
import { useAppSelector } from '../../redux/hooks';
import { showToast } from '../../services/toast.service';
import { fetchTransactionCartItems } from '../../services/transactionCart.service';
import { TransactionTypeEnum } from '../../types/enums';
import { Animal } from '../../types/interfaces';
import { pickInventoryListState } from './Inventory';

const InventoryWithdraw = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { animalId: paramAnimalId } = useParams();
  const listNavigationState = pickInventoryListState(location.state);

  const returnToInventoryItem = () => {
    if (paramAnimalId) {
      navigate(`${ROUTE_PATHS.APP_INVENTORY}/${paramAnimalId}`, { state: listNavigationState });
    } else {
      navigate(ROUTE_PATHS.APP_INVENTORY, { state: listNavigationState });
    }
  };

  const activeAccount = useAppSelector(state => state.activeAccount);
  const user = useAppSelector(state => state.user);
  const cartItems = useAppSelector(state => state.transactionCart.transactionCartItems);

  const [animalId, setAnimalId] = useState<string | undefined>(paramAnimalId?.toString() ?? paramAnimalId ?? '');
  const [selectedQuantities, setSelectedQuantities] = useState<{ valid: boolean; value: SelectedSpecimenQuantity[] }>({
    valid: false,
    value: [],
  });
  const [validate, setValidate] = useState<boolean>(false);
  const [specimenAddingToCart, setSpecimenAddingToCart] = useState<boolean>(true);
  const [selectedAnimal, setSelectedAnimal] = useState<FilterOption<Animal>>();
  const [animalOptions, setAnimalOptions] = useState<FilterOption<Animal>[]>([]);
  const [animal, setAnimal] = useState<Animal>();

  useEffect(() => {
    if (animalId) {
      fetchAnimal();
    } else {
      setAnimal(undefined);
    }
  }, [animalId]);

  const fetchAnimal = async () => {
    try {
      const response = await getAnimalById(animalId ?? '');
      setAnimal(response.data);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const isFemale = animal?.gender === 'Female';

  useEffect(() => {
    if (selectedAnimal?.value?.animalId) {
      setAnimalId(selectedAnimal?.value?.animalId?.toString());
    }
  }, [selectedAnimal]);

  useEffect(() => {
    const createFilterOptions = (animals: Animal[]) => {
      return animals.map((animal: Animal): FilterOption<Animal> => ({ name: `${animal.code}-${animal.name}`, value: animal }));
    };

    const getOwnedAnimals = async () => {
      setAnimalOptions([]);
      if (activeAccount?.accountId) {
        try {
          const { data } = await getAccountAnimals(activeAccount.accountId as number, {
            include: 'Breed.Species, Specimens, Owners',
          });

          const animalOptions = createFilterOptions(data as Animal[]);
          const animals = (data as Animal[]).find(animal => animal.animalId === Number(animalId));
          animals && setSelectedAnimal({ name: `${animals?.code}-${animals?.name}`, value: animals });
          setAnimalOptions(animalOptions);
        } catch {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };
    getOwnedAnimals();
  }, [activeAccount]);

  const isQuantityValid = () => {
    if (selectedQuantities.value.every(inventoryLot => inventoryLot.quantity === 0)) {
      showToast.error(toastMessages.INVALID_ZERO_QUANTITY);
      return false;
    } else if (selectedQuantities.valid) {
      return true;
    } else {
      return false;
    }
  };

  const isValid = () => {
    return activeAccount.accountId && animalId != undefined;
  };

  const isNextStepValid = () => {
    if (selectedQuantities.valid || (cartItems.length > 0 && !animalId)) {
      return true;
    } else {
      return false;
    }
  };

  const handleAddMoreSpecimen = () => {
    if (!specimenAddingToCart) return;
    setSpecimenAddingToCart(false);
    setValidate(true);

    if (isValid() && isQuantityValid()) {
      addSpecimenToCart();
      setSelectedAnimal(undefined);
      setAnimal(undefined);
      setSelectedQuantities(prev => ({ valid: false, value: prev.value }));
      setAnimalId('');
    } else {
      setSpecimenAddingToCart(true);
    }
  };

  const handleNextStep = async () => {
    if (!specimenAddingToCart) return;

    setSpecimenAddingToCart(false);

    if (isValid() && isQuantityValid()) {
      try {
        await addSpecimenToCart();
        navigate(ROUTE_PATHS.APP_TRANSACTION_CART_WITHDRAW + activeAccount?.accountId);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } else {
      setSpecimenAddingToCart(true);
      if (cartItems.length > 0 && !animalId) {
        navigate(ROUTE_PATHS.APP_TRANSACTION_CART_WITHDRAW + activeAccount?.accountId);
      }
    }
  };

  const addSpecimenToCart = async () => {
    try {
      const selectedSpecimens = {
        userId: user.userId,
        accountId: activeAccount?.accountId!,
        animalId: Number(animalId),
        specimenCartItems: selectedQuantities.value.map(specimen => {
          return {
            specimenId: specimen?.specimenId,
            transactionType: TransactionTypeEnum.Withdraw,
            quantity: specimen?.quantity,
            ...(isFemale && {
              selectedEmbryoStrawIds: specimen?.embryoStrawId ? [specimen.embryoStrawId] : [],
            }),
          };
        }),
      };
      await addSelectedSpecimenToCart(selectedSpecimens);
      fetchTransactionCartItems(user.userId, activeAccount.accountId);
      showToast.success(toastMessages.ADD_REQUEST_TO_CART_SUCCESS);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setSpecimenAddingToCart(true);
    }
  };

  const onAnimalChange = (animalId: number) => {
    navigate(ROUTE_PATHS.APP_INVENTORY + '/' + animalId + '/' + INVENTORY_PATHS.WITHDRAW, { state: listNavigationState });
  };

  return (
    <div className="inventory-withdraw">
      <BackButton onClick={returnToInventoryItem} />
      <div className="inventory-action card">
        <h1 className="withdraw-inventory-title">{PAGE_HEADER_CONSTANTS.WITHDRAW_INVENTORY}</h1>
        <p className="withdraw-inventory-guide">{INVENTORY_MANAGEMENT_CONSTANTS.WITHDRAW_INVENTORY_HEADER}</p>
        <div className="help-text-guide">
          <div className="attention-group-orders">{INVENTORY_MANAGEMENT_CONSTANTS.GROUP_ORDER_ATTENTION}</div>
          <p>
            If this is a group order with multiple clients or multiple parties shipping in the same tank, please contact our
            Genetics Team directly for assistance. You can reach us by phone at <a className="highlight">765-978-0301</a> or via
            email at <a className="highlight">genetics@reprospecialty.us</a>.
          </p>
        </div>
        <br />

        <div className="form-row">
          <label>{ANIMAL_CONSTANTS.ANIMAL}:</label>
          <div className="input-container">
            <FilterDropdown
              options={animalOptions}
              value={selectedAnimal ?? { name: '', value: undefined }}
              disabled={animalOptions.length === 0}
              onChange={e => {
                setSelectedAnimal(e);
                onAnimalChange(e?.value?.animalId);
              }}
            />
          </div>
        </div>

        <AnimalInfo animalId={animalId} showQuantityAvailable />
        <br />

        {!specimenAddingToCart && <Loader loaderSize={'small'} />}

        <h2>{LABEL_CONSTANTS.SELECT_ITEM_QUANTITY}</h2>
        {animal && (
          <SelectSpecimens
            animalId={+animalId!}
            accountId={activeAccount.accountId}
            animalGender={animal?.gender}
            onChange={e => setSelectedQuantities(e)}
          />
        )}
        <br />

        <div className="flex-right withdraw-submit-section">
          <div className="withdraw-submit-guide">
            <p className="withdraw-inventory-guide">{INVENTORY_MANAGEMENT_CONSTANTS.BULK_ORDER_ADD_MORE_FOOTER}</p>
            <p className="withdraw-inventory-guide">{INVENTORY_MANAGEMENT_CONSTANTS.BULK_ORDER_NEXT_STEP_FOOTER}</p>
          </div>
          <div className="buttons">
            <button
              className={selectedQuantities.valid ? 'button green' : 'button green disabled'}
              onClick={() => handleAddMoreSpecimen()}>
              {BUTTON_CONSTANTS.SAVE_AND_ADD_MORE}
            </button>
            <button
              className={isNextStepValid() ? 'button green small' : 'button green small disabled'}
              onClick={() => handleNextStep()}>
              {BUTTON_CONSTANTS.NEXT_STEP}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryWithdraw;
